import React, { useState, useRef, useEffect } from 'react';
import {
    ChevronLeft, ChevronRight, Image as ImageIcon,
    Video, Trash2, Ticket, Pencil, Plus, ExternalLink,
    ArrowLeftToLine, ArrowRightToLine, X, LinkIcon,
    LayoutGrid, CalendarDays
} from 'lucide-react';
import {kakaoImageUpload} from "../../api/file/file.api";
import {listBenefit} from "../../api/benefit/benefit.api";
import moment from "moment";
import {wideListItemLabel} from "../../libs/helper";

// --- [유효성 검사 규칙] ---
const RULES = {
    COMMON: { minImgWidth: 500 },
    BASE: { maxChar: 1300, maxLine: 99, nonMemberMaxChar: 300 },
    WIDE_IMG: { maxChar: 76, maxLine: 5 },
    WIDE_LIST: {
        titleMax: 20,
        firstItemChar: 25,
        otherItemChar: 30,
        minItems: 3, maxItems: 4, maxLine: 5
    },
    CAROUSEL: {
        minItems: 2, maxItems: 6,
        titleMax: 20, contentMax: 180, maxLine: 10
    },
    VIDEO: {
        titleMax: 20, contentMax: 76, maxLine: 5
    },
    BUTTON: {
        maxName: 8
    },
    COUPON: {
        maxName: 7
    }
};

/**
 * `highlightField`(부모의 검증 실패 항목 키)가 이 입력칸을 가리키면 강조 클래스를 돌려준다.
 * 안내 문구만으로는 어느 칸인지 못 찾겠다는 피드백(2026-07-29)에 따라 추가.
 * `dark` = 이미지 위 오버레이처럼 어두운 배경에 놓인 입력칸(메인 아이템 내용/링크).
 */
const highlightClass = (highlightField, key, dark = false) => {
    if (highlightField !== key) return '';
    return dark
        ? ' ring-2 ring-red-400 ring-offset-1 ring-offset-black/40 bg-red-500/40 rounded'
        : ' ring-2 ring-red-500 ring-offset-1 bg-red-50 rounded';
};

/**
 * 목록 렌더 key 용 안정적인 id 를 채워 준다.
 * 서버에서 불러온 items/carousels 에는 `id` 가 없어 `key={item.id}` 가 전부 `undefined` 가 되고,
 * React 가 "Each child in a list should have a unique key" 경고를 내며
 * 추가/삭제/순서변경 시 엉뚱한 행이 재사용된다.
 */
const withIds = (list, prefix) => list.map((row, i) => ({ ...row, id: row?.id ?? `${prefix}-${i + 1}` }));

const OneTimeMessageCreator = ({ msgType, onUpdate, initialData, isNonMember, highlightField = null }) => {
    const [header, setHeader] = useState(initialData?.header || '');
    const [content, setContent] = useState(initialData?.content || '');
    const [image, setImage] = useState(initialData?.image);
    const [video, setVideo] = useState(initialData?.video);

    /** 이 입력칸이 검증 실패 대상인지. */
    const hl = (key, dark = false) => highlightClass(highlightField, key, dark);

    const handleResizeHeight = (target) => {
        if (!target) return;
        target.style.height = 'auto';
        target.style.height = `${target.scrollHeight}px`;
    };

    // 초기값 주의: 입력칸이 읽는 키는 `title` 이다. 예전 시드는 쓰이지 않는 `content` 키에 기본 문구를 넣어
    // 화면에는 빈칸으로 보였고(그래서 메인 아이템을 안 채우고 넘어가기 쉬웠다), React 도 uncontrolled 경고를 냈다.
    // `id` 는 목록 렌더 key 용 — 서버에서 불러온 데이터에는 없으므로 **마운트 시 채워 넣는다**.
    // (없으면 형제 key 가 전부 undefined 가 되어 React 경고 + 추가/삭제/순서변경 시 행이 잘못 재사용된다.)
    const [items, setItems] = useState(() => withIds(initialData?.items ?? [
        { title: '', imgUrl: null, urlMobile: '' },
        { title: '', imgUrl: null, urlMobile: '' },
        { title: '', imgUrl: null, urlMobile: '' },
    ], 'item'));

    // 초기값 주의: 안내 문구는 **placeholder 로만** 보여주고 값은 비워 둔다.
    // 예전에는 `name: '자세히보기'`·`urlMobile: 'https://'` 처럼 값이 미리 채워져 있어
    // 안내 문구인 줄 알고 그대로 두면 **기본값이 실제로 발송**될 수 있었다(2026-07-29 피드백).
    // (`title`/`content` 키는 편집 화면이 `header`/`message` 를 읽으므로 애초에 쓰이지 않는 죽은 키였다.)
    const [carousels, setCarousels] = useState(() => withIds(initialData?.carousels ?? [
        {
            header: '', message: '',
            attachment: { image: null, buttons: [{ type: 'LINK', name: '', urlMobile: '' }] }
        },
        {
            header: '', message: '',
            attachment: { image: null, buttons: [{ type: 'LINK', name: '', urlMobile: '' }] }
        },
    ], 'feed'));

    const [buttons, setButtons] = useState(initialData?.buttons || []);

    const [currentSlide, setCurrentSlide] = useState(0);
    const carouselRef = useRef(null);

    const [isBtnModalOpen, setIsBtnModalOpen] = useState(false);
    /** 버튼 추가 모달이 어느 캐러셀 피드를 대상으로 열렸는지(수동 스크롤로 currentSlide 가 어긋나도 안전하게). */
    const [btnModalFeedIndex, setBtnModalFeedIndex] = useState(0);


    const [isAllFeedsModalOpen, setIsAllFeedsModalOpen] = useState(false);


    useEffect(() => {
        const coupon = initialData?.coupon

        if (['FI', 'FW', 'FL', 'FV'].includes(msgType) && coupon) {
            setButtons([...buttons, {
                type: 'COUPON',
                name: coupon.title.replace('무료 쿠폰', ''),
                description: coupon.description,
                benefitId: coupon.benefitId,
                benefitExpiredDay: coupon.benefitExpiredDay,
                benefitExpiredDate: coupon.benefitExpiredDate,
                urlMobile: coupon.urlMobile
            }])
        }
    }, []);

    useEffect(() => {
        if (onUpdate) {
            onUpdate({
                header,
                content,
                image,
                video,
                items,
                carousels,
                buttons
            });
        }
    }, [header, content, image, video, items, carousels, buttons, onUpdate]);

    /**
     * 본문 글자수 제한. **한 곳에서 파생시킨다.**
     *
     * 🔴 종전에는 세 곳이 따로 있었다 —
     *   placeholder "최대 400자"(하드코딩) / 카운터 `RULES.BASE.maxChar`(1300) /
     *   실제 절단 `isNonMember ? nonMemberMaxChar : maxChar`
     * 그래서 '기본 텍스트'에서 **안내는 400자, 카운터는 1300자**로 어긋났고,
     * 비회원을 켜면 실제 제한은 300자인데 표시는 여전히 400/1300 이었다(3중 불일치).
     *
     * 표시와 실제가 갈라지면 사용자는 어느 쪽을 믿어야 할지 알 수 없다 → 하나로 묶는다.
     */
    const contentLimit = (() => {
        const rule = msgType === 'FI' ? RULES.BASE : RULES.WIDE_IMG;
        return isNonMember && rule.nonMemberMaxChar ? rule.nonMemberMaxChar : rule.maxChar;
    })();

    // 비회원 친구를 포함하면 본문 길이 제한이 더 짧아진다(모먼트 규격) → 초과분을 잘라준다.
    //
    // 🔴 종전에는 `initialData.content` 를 읽어 **null 이면 터졌다.**
    //    재현: 기본 텍스트로 작성 → 비회원 ON → 뒤로가기 → 메시지 타입 변경
    //    → `OneTimeMessagePage` 가 `setCreatorData(null)`(타입이 바뀌면 데이터 초기화)
    //    → 이 컴포넌트가 `initialData = null` 로 다시 마운트
    //    → `isNonMember` 가 true 라 이 effect 가 즉시 실행 → `null.content` → 흰 화면.
    //
    //    고칠 지점은 두 가지였다.
    //    ① `initialData` 대신 **현재 편집 중인 `content`** 를 자르는 것이 옳다.
    //       타입을 바꿔 초기화된 뒤에 옛 initialData 를 되살리면 지운 내용이 돌아온다.
    //    ② 값이 없으면 아무것도 하지 않는다(substringByWeightedLength 도 빈 문자열을 돌려준다).
    useEffect(() => {
        if (!isNonMember) return;
        setContent(prev => (prev ? substringByWeightedLength(prev, 300) : prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNonMember]);

    const getWeightedLength = (str) => {
        if (!str) return 0;
        const emojiRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27FF]/g;
        return str.replace(emojiRegex, "aaa").length;
    };

    const substringByWeightedLength = (str, maxLength = 300) => {
        if (!str) return "";

        let result = "";

        for (const char of str) {
            const next = result + char;

            if (getWeightedLength(next) > maxLength) {
                break;
            }

            result = next;
        }

        return result;
    };

    const handleImageUpload = (e, callback) => {
        const file = e.target.files?.[0];
        if (!file) return;

        kakaoImageUpload(file).then(({data}) => {
            if (!data?.imageUrl) {
                alert(data.message)
            } else {
                callback(data?.imageUrl)
            }
        })
    }

    const handleTextChange = (text, maxChar, maxLine) => {
        const currentLen = getWeightedLength(text);
        if (currentLen > maxChar) return;
        if (maxLine && text.split('\n').length - 1 > maxLine) return;
        return text;
    };

    const moveSlide = (direction) => {
        let nextIndex = currentSlide;
        if (direction === 'prev') {
            nextIndex = Math.max(0, currentSlide - 1);
        } else {
            nextIndex = Math.min(carousels.length - 1, currentSlide + 1);
        }
        setCurrentSlide(nextIndex);
    };

    useEffect(() => {
        if (carouselRef.current) {
            const scrollAmount = carouselRef.current.offsetWidth * currentSlide;
            carouselRef.current.scrollTo({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    }, [currentSlide]);

    const handleReorder = (index, direction) => {
        const newItems = [...carousels];
        const targetIndex = direction === 'left' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newItems.length) return;

        [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];

        setCarousels(newItems);
        setCurrentSlide(targetIndex);
    };

    // --- [버튼 관리 로직 수정됨] ---
    const handleAddButton = (type, data = null) => {
        let newBtn = null;

        if (type === 'LINK') {
            // 일반 웹 링크 버튼.
            // 이름/URL 을 **비워서** 추가한다 — 안내 문구는 placeholder 가 보여준다.
            // 예전에는 `name: '버튼명'`, `urlMobile: 'https://'` 가 값으로 들어가 있어
            // 사용자가 안내 문구로 착각하고 그대로 두면 버튼명이 "버튼명"인 채로 발송될 수 있었다.
            newBtn = { type, name: '', urlMobile: '' };
        } else if (type === 'COUPON') {
            // 쿠폰 버튼 (첨부 모드 vs URL 모드). 이름/설명도 같은 이유로 비워서 추가한다.
            newBtn = {
                type,
                name: '',
                description: '',
                // 모드에 따라 데이터 분기
                ...(data?.mode === 'ATTACH' ? {
                    benefitId: data.benefitId,
                    benefitExpiredDay: data.benefitExpiredDay,
                    benefitExpiredDate: data.benefitExpiredDate
                } : {
                    urlMobile: data.urlMobile
                })            };
        }

        // ... (기존 개수 제한, 중복 체크, 정렬 로직 동일) ...
        const targetFeed = btnModalFeedIndex;
        let currentList = msgType === 'FC'
            ? [...(carousels[targetFeed]?.attachment?.buttons ?? [])]
            : [...(buttons ?? [])];

        if (currentList.length >= 2) return alert('버튼은 최대 2개까지만 가능합니다.');

        const hasCoupon = currentList.some(btn => btn.type === 'COUPON');
        if (type === 'COUPON' && hasCoupon) return alert('혜택은 1개만 추가할 수 있습니다.');

        if (type === 'COUPON') {
            currentList.push(newBtn);
        } else {
            const couponIndex = currentList.findIndex(btn => btn.type === 'COUPON');
            if (couponIndex !== -1) currentList.splice(couponIndex, 0, newBtn);
            else currentList.push(newBtn);
        }

        // 상태 업데이트
        if (msgType === 'FC') {
            const newItems = [...carousels];
            if (!newItems[targetFeed]?.attachment) return;
            newItems[targetFeed].attachment.buttons = currentList;
            setCarousels(newItems);
        } else {
            setButtons(currentList);
        }

        setIsBtnModalOpen(false);
    };

    // feedIndex: 캐러셀에서 대상 피드. 넘기지 않으면 currentSlide 로 폴백한다.
    // (수동 스크롤은 currentSlide 를 갱신하지 않으므로, 보이는 피드와 어긋나면 엉뚱한 피드를 고치게 된다.)
    const updateButton = (index, key, value, isCarousel = false, feedIndex = currentSlide) => {
        if (key === 'name') {
            if (getWeightedLength(value) > RULES.BUTTON.maxName) return;
        }
        if (key === 'description') {
            if (getWeightedLength(value) > (['FW', 'FL', 'FP'].includes(msgType) ? 18 : 12)) return;
        }

        if (isCarousel) {
            const newItems = [...carousels];
            if (!newItems[feedIndex]?.attachment?.buttons?.[index]) return;
            newItems[feedIndex].attachment.buttons[index][key] = value;
            setCarousels(newItems);
        } else {
            const newBtns = [...buttons];
            newBtns[index][key] = value;
            setButtons(newBtns);
        }
    };

    const removeButton = (index, isCarousel = false, feedIndex = currentSlide) => {
        if (isCarousel) {
            // 캐러셀 버튼은 `carousels[i].attachment.buttons` 에 있다.
            // 예전 코드는 `carousels[i].buttons`(존재하지 않는 경로)를 읽어
            // "Cannot read properties of undefined (reading 'length')" 로 삭제가 아예 불가능했다.
            const newItems = [...carousels];
            const feedButtons = newItems[feedIndex]?.attachment?.buttons ?? [];
            if (feedButtons.length <= 1) {
                if(!window.confirm('캐러셀 피드는 최소 1개의 버튼이 필요합니다. 정말 삭제하시겠습니까?')) return;
            }
            newItems[feedIndex].attachment.buttons = feedButtons.filter((_, i) => i !== index);
            setCarousels(newItems);
        } else {
            setButtons((buttons ?? []).filter((_, i) => i !== index));
        }
    };

    // --- [UI 컴포넌트: 전체 피드 보기 모달] ---
    const AllFeedsModal = () => {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
                <div className="bg-white w-full max-w-4xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                    {/* 헤더 */}
                    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                        <div className="flex items-center gap-2">
                            <LayoutGrid size={20} className="text-indigo-600"/>
                            <h3 className="text-lg font-bold text-gray-800">전체 피드 보기 ({carousels.length})</h3>
                        </div>
                        <button onClick={() => setIsAllFeedsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                            <X size={24} />
                        </button>
                    </div>

                    {/* 컨텐츠 (그리드) */}
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {carousels.map((feed, idx) => (
                                <div
                                    key={feed.id ?? `feed-${idx}`}
                                    onClick={() => {
                                        setCurrentSlide(idx); // 클릭 시 해당 슬라이드로 이동
                                        setIsAllFeedsModalOpen(false);
                                    }}
                                    className={`bg-white rounded-xl overflow-hidden shadow-sm border-2 cursor-pointer transition-all hover:shadow-md hover:-translate-y-1
                                        ${currentSlide === idx ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-transparent hover:border-indigo-300'}
                                    `}
                                >
                                    {/* 피드 헤더 (번호) */}
                                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex justify-between items-center">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${currentSlide === idx ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-500'}`}>
                                            #{idx + 1}
                                        </span>
                                        {currentSlide === idx && <span className="text-[10px] text-indigo-500 font-medium">편집 중</span>}
                                    </div>

                                    {/* 이미지 미리보기 */}
                                    <div className="aspect-square bg-gray-100 relative flex items-center justify-center">
                                        {feed.attachment.image ? (
                                            <img src={feed.attachment.image.imgUrl} alt={`feed-${idx}`} className="w-full" />
                                        ) : (
                                            <ImageIcon className="text-gray-300" size={24} />
                                        )}
                                    </div>

                                    {/* 텍스트 미리보기 */}
                                    <div className="p-3 space-y-1">
                                        <div className="text-sm font-bold text-gray-800 truncate">
                                            {feed.header || <span className="text-gray-300">타이틀 없음</span>}
                                        </div>
                                        <div className="text-xs text-gray-500 whitespace-pre-line">
                                            {feed.message || <span className="text-gray-300">내용 없음</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- [UI 컴포넌트: 버튼 추가 모달] ---
    const AddButtonModal = () => {
        // 캐러셀의 msgType 은 'FC' 다. 예전 코드는 존재하지 않는 'CAROUSEL_FEED' 와 비교하고
        // `carousels[i].buttons`(잘못된 경로)를 읽어, 캐러셀에서는 쿠폰 중복 판정이 늘 빗나갔다.
        const currentList = (msgType === 'FC'
            ? carousels[btnModalFeedIndex]?.attachment?.buttons
            : buttons) ?? [];
        const hasCoupon = currentList.some(btn => btn.type === 'COUPON');
        // 모달 내부 상태
        const [showCouponSettings, setShowCouponSettings] = useState(false);
        const [couponConfig, setCouponConfig] = useState({
            periodType: 'issue'
        });

        /**
         * '확인 (추가)' 활성 조건 — 혜택 목록에서 고른 혜택(benefitId)과 유효기간이 있어야 한다.
         *
         * 구 'URL 입력' 탭 폐기(2026-07-30 사용자 결정): benefitId 없이 URL 만 넣는 쿠폰은
         * `benefit_result` 가 생성되지 않아 **발급 기록·개인별 URL·유효기간·암호가 전부 없다**.
         * 즉 일반 '웹 링크 버튼' 과 추적상 차이가 없으면서 비회원(모먼트) 발송만 차단돼 오히려 불리했다.
         * URL 로 주고 싶은 혜택은 '링크 혜택형' 혜택으로 등록해 여기서 첨부하면 된다.
         * (기존에 URL 모드로 저장된 메시지는 그대로 열리고 발송된다 — 신규 생성만 막는다.)
         */
        const isCouponReady = !!couponConfig.benefitId
            && !(couponConfig.periodType === 'issue' && !couponConfig.benefitExpiredDay)
            && !(couponConfig.periodType === 'date' && !couponConfig.benefitExpiredDate);

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl w-96 shadow-2xl animate-fadeIn transition-all">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold">버튼 추가</h3>
                        <button onClick={() => setIsBtnModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                    </div>

                    {/* 1. 웹 링크 버튼 */}
                    <div className="mb-3">
                        <button
                            onClick={() => handleAddButton('LINK')}
                            className="w-full p-3 border border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 flex items-center justify-center text-gray-700 transition-colors group"
                        >
                            <LinkIcon className="mr-2 text-gray-400 group-hover:text-indigo-500" size={18} />
                            <span>웹 링크 (URL) 버튼 추가</span>
                        </button>
                    </div>

                    <div className="border-t border-gray-100 my-4 relative">
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-gray-400">OR</span>
                    </div>

                    {/* 2. 쿠폰 버튼 (설정 UI 포함) */}
                    <div className="space-y-3">
                        {!showCouponSettings ? (
                            <button
                                onClick={() => setShowCouponSettings(true)}
                                disabled={hasCoupon}
                                className={`w-full p-3 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                                    ${hasCoupon
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : 'border-gray-300 hover:border-yellow-400 hover:bg-yellow-50 text-gray-600 cursor-pointer'}
                                `}
                            >
                                <Ticket className="mr-2" size={18} />
                                <span>{hasCoupon ? '혜택은 최대 1개까지 가능합니다' : '쿠폰/혜택 버튼 추가'}</span>
                            </button>
                        ) : (
                            <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">

                                <BenefitSelector couponConfig={couponConfig} setCouponConfig={setCouponConfig} />

                                {/* 하단 버튼 */}
                                <div className="flex gap-2 pt-2">
                                    <button
                                        onClick={() => setShowCouponSettings(false)}
                                        className="flex-1 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
                                    >
                                        취소
                                    </button>
                                    <button
                                        disabled={!isCouponReady}
                                        onClick={() => handleAddButton('COUPON', { ...couponConfig, mode: 'ATTACH' })}
                                        className={`${!isCouponReady
                                            ? 'bg-gray-200 text-gray-300' : 'bg-blue-500 hover:bg-blue-600 text-white'} flex-1 py-2 text-xs rounded-lg font-bold`}
                                    >
                                        확인 (추가)
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- [UI 렌더링: 버튼 목록] ---
    // keyPrefix: 강조 표시용 필드 키 접두사. 캐러셀 피드 버튼은 `carousel.<i>.` 가 붙는다
    // (validateMessageContent 가 만드는 키와 같아야 한다).
    const renderButtonList = (targetButtons, isCarouselFeed = false, keyPrefix = "", feedIndex = currentSlide) => {
        return (
            <div className="bg-white mt-1 rounded-lg overflow-hidden shadow-sm divide-y divide-gray-100 border border-gray-100">
                {targetButtons.map((btn, idx) => {
                  const btnKey = `${keyPrefix}button.${idx}`;
                  return (
                    <div key={idx} className="relative group p-2">

                        {btn.type === 'COUPON' ? (
                            <div className="kakao_link_coupon bg-yellow-50/50 p-2 rounded-lg border border-yellow-100 relative">
                                <div className="kakao_coupon flex flex-col">
                                    <div className="flex items-center mb-1">
                                        <div className="rounded-lg relative group/edit w-max mr-2">
                                            <input
                                                type="text"
                                                className={`w-[90px] bg-transparent font-bold text-gray-800 outline-none border-b border-transparent focus:border-indigo-300 transition-all${hl(`${btnKey}.name`)}`}
                                                placeholder="쿠폰받기"
                                                value={btn.name ?? ''}
                                                onChange={(e) => updateButton(idx, 'name', e.target.value, isCarouselFeed, feedIndex)}
                                            />
                                            <div className="absolute -right-3 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 group-hover/edit:opacity-100 transition-opacity pointer-events-none">
                                                <Pencil size={10} />
                                            </div>
                                        </div>
                                        <div className="text-[10px] text-gray-400 bg-white px-1 rounded border border-gray-100 flex-shrink-0">무료 쿠폰</div>

                                        {/* [New] 쿠폰 버튼명 글자수 표시 */}
                                        <span className="text-[9px] text-gray-400 ml-1.5 flex-shrink-0">
                                            {getWeightedLength(btn.name)}/{RULES.COUPON.maxName}
                                        </span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                        <div className="desc text-xs text-gray-500">
                                            <div className="rounded-lg relative group/edit w-full">
                                                <input
                                                    type="text"
                                                    className={`w-full bg-transparent outline-none border-b border-transparent focus:border-indigo-300 transition-all${hl(`${btnKey}.description`)}`}
                                                    placeholder="설명 입력"
                                                    value={btn.description || ''}
                                                    onChange={(e) => updateButton(idx, 'description', e.target.value, isCarouselFeed, feedIndex)}
                                                />
                                                <div className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 group-hover/edit:opacity-100 transition-opacity pointer-events-none">
                                                    <Pencil size={10} />
                                                </div>
                                            </div>
                                        </div>
                                        <span className="text-[9px] text-gray-400 ml-1.5 flex-shrink-0">
                                                {getWeightedLength(btn.description)}/{['FW', 'FL', 'FP'].includes(msgType) ? 18 : 12}
                                            </span>
                                    </div>
                                    {/* [Info] 쿠폰 타입에 따라 정보 표시 */}
                                    <div className="text-[10px] text-blue-600 mt-1 truncate">
                                        {btn.benefitId
                                            ? `${btn?.name || '쿠폰'} ${btn.benefitExpiredDay ? `(발급 후 ${btn.benefitExpiredDay}일)` : `(${btn.benefitExpiredDate}까지)`} `
                                            : `URL: ${btn.urlMobile}`
                                        }
                                    </div>
                                </div>
                                <span className="kakao-icon icon_coupon"></span>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                                <div className="relative group/edit flex items-center ml-2">
                                    <input
                                        type="text"
                                        className={`text-center bg-transparent text-sm text-gray-700 font-medium outline-none border-b border-transparent focus:border-indigo-300 w-32 transition-all${hl(`${btnKey}.name`)}`}
                                        placeholder="자세히보기"
                                        value={btn.name ?? ''}
                                        onChange={(e) => updateButton(idx, 'name', e.target.value, isCarouselFeed, feedIndex)}
                                    />
                                    <div className="absolute -right-4 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 group-hover/edit:opacity-100 transition-opacity pointer-events-none">
                                        <Pencil size={10} />
                                    </div>

                                    {/* [New] 일반 버튼명 글자수 표시 */}
                                    <div className="text-[9px] text-gray-400 mt-0.5">
                                        {getWeightedLength(btn.name)}/{RULES.BUTTON.maxName}
                                    </div>
                                </div>

                                <div className="relative group/edit w-full px-4 mt-1">
                                    <input
                                        type="text"
                                        className={`w-full text-center bg-transparent text-[10px] text-gray-400 outline-none border-b border-transparent focus:border-indigo-300 transition-all${hl(`${btnKey}.urlMobile`)}`}
                                        placeholder="링크 URL (https:// 포함)"
                                        value={btn.urlMobile ?? ''}
                                        onChange={(e) => updateButton(idx, 'urlMobile', e.target.value, isCarouselFeed, feedIndex)}
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 group-hover/edit:opacity-100 transition-opacity pointer-events-none">
                                        <Pencil size={8} />
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => removeButton(idx, isCarouselFeed, feedIndex)}
                            className="absolute right-1 top-1 p-1 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10"
                            title="버튼 삭제">
                            <Trash2 size={14} />
                        </button>
                    </div>
                  );
                })}

                {targetButtons.length < 2 && (
                    <button
                        onClick={() => { setBtnModalFeedIndex(feedIndex); setIsBtnModalOpen(true); }}
                        className={`w-full py-3 text-center text-sm font-bold text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition-colors border-t border-dashed border-gray-200${hl(`${keyPrefix}button.add`)}`}
                    >
                        + 버튼 추가
                    </button>
                )}
            </div>
        )
    };

    // --- [렌더러: 타입별 본문] ---

    // 1. 기본 텍스트 & 와이드 이미지
    const renderBaseOrWide = () => (
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
            <label className={`block bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors relative group
        ${msgType === 'FW' ? 'aspect-[2/1] min-w-[300px]' : 'aspect-[4/3] w-[256px] '}${hl('image')}`}>
                {image ? (
                    <img src={image.imgUrl} alt="Main" className="w-full" />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <ImageIcon size={32} />
                        <span className="text-xs mt-2">이미지 업로드</span>
                        {
                            msgType === 'FW' ?
                                <span className="text-xs mt-2">(가로 최소 500px↑, 최소 2:1, 최대 3:4 비율)</span>
                                : <span className="text-xs mt-2">(가로 최소 500px↑, 최소 2:1, 최대 1:1 비율)</span>
                        }

                    </div>
                )}
                <input type="file" className="hidden" accept="image/png, image/jpeg" onChange={(e) => handleImageUpload(e, (url) => {
                    setImage({imgUrl: url})
                })} />
            </label>
            <div className="p-4">
                <textarea
                    // [New] ref를 사용하여 초기 렌더링 시 높이 조절
                    ref={(el) => handleResizeHeight(el)}
                    className={`w-full resize-none outline-none text-sm placeholder-gray-400 bg-transparent overflow-hidden${hl('content')}`}
                    placeholder={`내용을 입력해주세요 (최대 ${contentLimit}자)`}
                    rows={msgType === 'FI' ? 5 : 2} // 기본 높이는 유지하되 내용이 많으면 늘어남
                    value={content}
                    // [New] 입력 시마다 높이 조절 함수 호출
                    onInput={(e) => handleResizeHeight(e.target)}
                    onChange={(e) => {
                        const limit = msgType === 'FI' ? RULES.BASE : RULES.WIDE_IMG;
                        const val = handleTextChange(e.target.value, contentLimit, limit.maxLine);
                        if(val !== undefined) setContent(val);
                    }}
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                    {getWeightedLength(content)} / {contentLimit}
                </div>
            </div>
        </div>
    );

    // 2. 와이드 리스트
    const renderWideList = () => (
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
            {/* 타이틀 영역 */}
            <div className="flex p-4 border-b border-gray-100">
                <input
                    type="text"
                    className={`flex-1 text-center font-bold text-gray-800 outline-none placeholder-gray-300${hl('header')}`}
                    placeholder="타이틀 (20자 이내)"
                    value={header}
                    onChange={(e) => {
                        const val = handleTextChange(e.target.value, RULES.WIDE_LIST.titleMax);
                        if(val !== undefined) setHeader(val);
                    }}
                />
                <div className="flex items-center text-right text-[10px] text-gray-400">
                    {getWeightedLength(header)} / 20
                </div>
            </div>

            {/* 첫 번째 리스트 아이템 (큰 이미지) = 메인 아이템 */}
            <div className={`relative aspect-[2/1] bg-gray-200 group${hl('item.0.imgUrl')}`}>
                <label className="flex items-center justify-center w-full h-full cursor-pointer hover:bg-gray-300 transition-colors">
                    {items[0].imgUrl ? (
                        <img src={items[0].imgUrl} alt="list1" className="w-full h-full object-cover" />
                    ) : (
                            <div className="text-center">
                                <span className="text-[10px] text-gray-500">가로 최소 500px↑ 2:1 비율</span>
                                <ImageIcon className="text-gray-400 mx-auto mb-16" />
                            </div>
                    )}
                        <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={(e) => handleImageUpload(e, (url) => {
                            const newItems = [...items]; newItems[0].imgUrl = url; setItems(newItems);
                        })} />
            </label>

                {/* 순서 라벨 — 채워진 뒤에도 몇 번째 항목인지 보이게 한다(placeholder 는 비어 있을 때만 보인다). */}
                <div className="absolute top-2 left-2 text-[10px] font-bold text-white bg-black/50 px-2 py-0.5 rounded-full pointer-events-none">
                    {wideListItemLabel(0)}
                </div>

                {/* 텍스트 및 URL 오버레이 */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 flex flex-col justify-end">
                    {/* 이미지 위 흰 글씨는 밝은 이미지에서 안 보인다는 피드백(2026-07-29) →
                        입력칸 자체에 반투명 검정 배경을 깔고 placeholder 를 명시한다. */}
                    <div className={`flex mb-1 bg-black/40 rounded px-2 py-1${hl('item.0.title', true)}`}>
                        <input
                            type="text"
                            className="flex-1 bg-transparent text-white font-medium outline-none placeholder-white/80"
                            placeholder="메인 아이템 내용 (필수)"
                            value={items[0].title ?? ''}
                            onChange={(e) => {
                                const val = handleTextChange(e.target.value, RULES.WIDE_LIST.firstItemChar);
                                if (val !== undefined) {
                                    const newItems = [...items]; newItems[0].title = val; setItems(newItems);
                                }
                            }}
                        />
                        <div className="text-right text-[10px] text-white flex items-center">
                            {getWeightedLength(items[0].title)} / {RULES.WIDE_LIST.firstItemChar}
                        </div>
                    </div>
                    {/* [New] URL 입력 필드 */}
                    <div className={`flex items-center text-white/80 text-xs bg-black/40 rounded px-2 py-1 mt-1${hl('item.0.urlMobile', true)}`}>
                        <LinkIcon size={10} className="mr-1" />
                        <input
                            type="text"
                            className="w-full bg-transparent outline-none placeholder-white/60 text-[10px]"
                            placeholder="링크 URL (https:// 포함, 필수)"
                            value={items[0].urlMobile ?? ''}
                            onChange={(e) => {
                                const newItems = [...items]; newItems[0].urlMobile = e.target.value; setItems(newItems);
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 나머지 리스트 아이템 */}
            <div className="divide-y divide-gray-100">
                {items.slice(1).map((item, idx) => {
                    // items 기준 실제 인덱스. 라벨·강조 키·onChange 가 모두 이 값을 쓴다
                    // (예전에는 placeholder 만 `idx+2` 를 따로 계산해 검증 메시지와 번호가 어긋났다).
                    const itemIndex = idx + 1;
                    return (
                    <div key={item.id ?? `item-${itemIndex}`} className="flex items-start p-3">
                        <label className={`w-14 h-14 bg-gray-100 rounded-md flex-shrink-0 cursor-pointer overflow-hidden relative mr-3 hover:opacity-80 mt-1${hl(`item.${itemIndex}.imgUrl`)}`}>
                            {item.imgUrl ? (
                                <img src={item.imgUrl} alt="thumb" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-center">
                                    <ImageIcon className="w-6 h-6 m-auto text-gray-300 relative top-3" />
                                    <span className="text-[8px] text-gray-400 relative top-1">1:1 비율</span>
                                </div>
                            )}
                            <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={(e) => handleImageUpload(e, (url) => {
                                const newItems = [...items]; newItems[itemIndex].imgUrl = url; setItems(newItems);
                            })} />
                        </label>

                        <div className="flex-1 flex flex-col justify-center min-h-[3.5rem]">
                            <div className="text-[10px] font-bold text-gray-400 mb-0.5">{wideListItemLabel(itemIndex)}</div>
                            <div className={`flex mb-1${hl(`item.${itemIndex}.title`)}`}>
                                <input
                                    type="text"
                                    className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                                    placeholder={`${wideListItemLabel(itemIndex)} 내용 (필수)`}
                                    value={item.title ?? ''}
                                    onChange={(e) => {
                                        const val = handleTextChange(e.target.value, RULES.WIDE_LIST.otherItemChar);
                                        if(val !== undefined) {
                                            const newItems = [...items]; newItems[itemIndex].title = val; setItems(newItems);
                                        }
                                    }}
                                />
                                <div className="text-right text-[10px] text-gray-400 flex items-center">
                                    {getWeightedLength(item.title)} / {RULES.WIDE_LIST.otherItemChar}
                                </div>
                            </div>
                            {/* [New] URL 입력 필드 */}
                            <div className={`flex items-center text-gray-400 text-[10px] bg-gray-50 rounded px-1.5 py-0.5 w-full${hl(`item.${itemIndex}.urlMobile`)}`}>
                                <LinkIcon size={10} className="mr-1 flex-shrink-0" />
                                <input
                                    type="text"
                                    className="w-full bg-transparent outline-none placeholder-gray-300"
                                    placeholder="링크 URL (https:// 포함, 필수)"
                                    value={item.urlMobile ?? ''}
                                    onChange={(e) => {
                                        const newItems = [...items]; newItems[itemIndex].urlMobile = e.target.value; setItems(newItems);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>

            {items.length < 4 && (
                <button onClick={() => setItems([...items, { id: `item-${Date.now()}`, title: '', imgUrl: null, urlMobile: '' }])}
                        className={`w-full py-2 text-xs text-indigo-500 bg-indigo-50 border-t border-indigo-100 hover:bg-indigo-100 transition-colors${hl('item.add')}`}>
                    + 리스트 추가 (최대 4개)
                </button>
            )}
            {items.length > 3 && (
                <button onClick={() => setItems(items.slice(0, -1))}
                        className="w-full py-2 text-xs text-red-500 bg-red-50 border-t border-red-100 hover:bg-red-100 transition-colors">
                    - 마지막 리스트 삭제
                </button>
            )}
        </div>
    );

    // 3. 캐러셀 피드
    //
    // 🔴 **여기에는 '피드 순서 변경' 만 있다.** 이전/다음 피드로 넘기는 화살표와
    //    '피드 추가'·'전체 피드 보기' 는 미리보기 프레임 **밖**으로 옮겼다
    //    (아래 [renderCarouselNav] / [renderCarouselFeedTools], 2026-09-10 사용자 지시).
    //
    //    왜 = 종전에는 피드 카드 위에 얹힌 **순서 변경** 화살표(◀ ▶)와 프레임 안 아래쪽의
    //    **피드 이동** 화살표(‹ ›)가 둘 다 '그냥 화살표' 였다. 순서 변경 쪽을 누르면
    //    다음 피드로 넘어갈 거라 착각해 **피드 순서를 모르고 바꾸는** 일이 있었다.
    //    ⇒ 순서 변경에는 **글자를 붙였고**(아이콘만으로는 구분이 안 된다),
    //       피드 이동은 미리보기 양옆이라는 **위치**로 구분한다.
    const renderCarousel = () => {
        return (
            <div className="relative">
                {/*
                  안내 문구를 뺐다(2026-09-10 2차). 버튼에 '순서 앞으로 / 순서 뒤로' 라고
                  적혀 있으면 설명이 필요 없다 — **버튼이 스스로 설명하는 것이 문구보다 낫다.**
                  (설명이 필요하다고 느껴지면 문구를 되살리기 전에 버튼 이름을 의심할 것.)
                */}
                {/* 캐러셀 컨테이너 */}
                <div
                    ref={carouselRef}
                    className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 px-1 hide-scrollbar"
                >
                    {carousels.map((item, index) => {
                        const attachment = item.attachment

                        return (
                            <div key={item.id ?? `feed-${index}`} className="relative flex-shrink-0 w-full snap-center">

                                <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 transition-all relative group">

                                    {/*
                                      [피드 순서 변경 컨트롤러]

                                      🔑 **글자를 넣었다.** 아이콘만으로는 '순서 바꾸기' 와 '다음 피드 보기' 가
                                         구분되지 않아 순서를 모르고 바꾸는 일이 있었다(2026-09-10 사용자 신고).
                                         아이콘도 `ArrowLeftCircle`(원형 화살표 = 이동으로 읽힌다) →
                                         `ArrowLeftToLine`(끝으로 밀어 넣는 모양)으로 바꿨다.

                                      🔑 **hover 로 숨기지 않는다.** 종전 `opacity-0 group-hover:opacity-100` 은
                                         마우스를 올려야 나타나서, 있는 줄 모르거나 반대로 이미지를 만지다
                                         갑자기 나타난 버튼을 누르게 됐다.
                                    */}
                                    <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-2 bg-gradient-to-b from-black/40 to-transparent">
                                        {index > 0 ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleReorder(index, 'left'); }}
                                                className="flex items-center gap-1 bg-white/95 pl-1.5 pr-2 py-1 rounded-full shadow-sm hover:bg-white text-gray-700 text-[10px] font-bold transition-colors"
                                                title="이 피드를 한 칸 앞 순서로 옮긴다 (보고 있는 피드가 바뀌는 것이 아니다)"
                                            >
                                                <ArrowLeftToLine size={12} /> 순서 앞으로
                                            </button>
                                        ) : <div className="w-[78px]" />}

                                        <div className="text-white text-[10px] font-bold bg-black/40 px-2 py-0.5 rounded-full">
                                            {index + 1} / {carousels.length}
                                        </div>

                                        {index < carousels.length - 1 ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleReorder(index, 'right'); }}
                                                className="flex items-center gap-1 bg-white/95 pl-2 pr-1.5 py-1 rounded-full shadow-sm hover:bg-white text-gray-700 text-[10px] font-bold transition-colors"
                                                title="이 피드를 한 칸 뒤 순서로 옮긴다 (보고 있는 피드가 바뀌는 것이 아니다)"
                                            >
                                                순서 뒤로 <ArrowRightToLine size={12} />
                                            </button>
                                        ) : <div className="w-[78px]" />}
                                    </div>

                                    {/* 이미지 */}
                                    <label className={`block aspect-square bg-gray-100 cursor-pointer relative group hover:bg-gray-200 transition-colors${hl(`carousel.${index}.image`)}`}>
                                        {attachment.image ? (
                                            <img src={attachment.image.imgUrl} alt="carousel" className="w-full" />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                                <ImageIcon size={32} />
                                                <span className="text-xs mt-2">이미지 업로드</span>
                                                <span className="text-xs mt-2">(가로 최소 500px↑, 최소 2:1, 최대 3:4 비율)</span>
                                            </div>
                                        )}
                                        <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={(e) => handleImageUpload(e, (url) => {
                                            const newItems = [...carousels]; newItems[index].attachment.image = {imgUrl: url}; setCarousels(newItems);
                                        })} />
                                    </label>

                                    {/* 텍스트 내용 */}
                                    <div className="p-4">
                                        <input
                                            className={`w-full font-bold text-gray-800 mb-1 outline-none placeholder-gray-300${hl(`carousel.${index}.header`)}`}
                                            placeholder="타이틀 (20자)"
                                            value={item.header ?? ''}
                                            onChange={(e) => {
                                                const val = handleTextChange(e.target.value, RULES.CAROUSEL.titleMax);
                                                if(val !== undefined) {
                                                    const newItems = [...carousels]; newItems[index].header = val; setCarousels(newItems);
                                                }
                                            }}
                                        />
                                        <div className="text-right text-[10px] text-gray-400 mb-1">
                                            {getWeightedLength(item.header)} / 20
                                        </div>

                                        <textarea
                                            // [New] ref를 사용하여 초기 렌더링 및 슬라이드 변경 시 높이 조절
                                            ref={(el) => handleResizeHeight(el)}
                                            className={`w-full resize-none text-sm text-gray-600 outline-none placeholder-gray-300 bg-transparent overflow-hidden${hl(`carousel.${index}.message`)}`} // overflow-hidden, h-20 제거하고 min-h 설정 권장
                                            style={{ minHeight: '5rem' }} // 기본 높이 설정
                                            placeholder="내용 (180자)"
                                            value={item.message ?? ''}
                                            onInput={(e) => handleResizeHeight(e.target)}
                                            onChange={(e) => {
                                                const val = handleTextChange(e.target.value, RULES.CAROUSEL.contentMax, RULES.CAROUSEL.maxLine);
                                                if(val !== undefined) {
                                                    const newItems = [...carousels]; newItems[index].message = val; setCarousels(newItems);
                                                }
                                            }}
                                        />
                                        <div className="text-right text-[10px] text-gray-400">
                                            {getWeightedLength(item.message)} / 180
                                        </div>
                                    </div>

                                    {/* 피드별 버튼 리스트 */}
                                    <div className="px-4 pb-4">
                                        {renderButtonList(attachment.buttons, true, `carousel.${index}.`, index)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    /**
     * 미리보기 **양옆**의 이전/다음 피드 화살표.
     *
     * 종전에는 프레임 **안 아래쪽**에 있어서, 위쪽 카드에 얹힌 '순서 변경' 화살표와
     * 역할이 헷갈렸다. 양옆으로 내보내면 위치만으로 "화면을 넘기는 것" 으로 읽힌다
     * (2026-09-10 사용자 지시).
     *
     * 🔑 프레임 **안쪽 가장자리**에 붙인다(`left-1`/`right-1`). 프레임 밖으로 반쯤 빼면
     *    좌우 여백(`p-4 md:p-8`)이 좁은 화면에서 잘린다.
     */
    const renderCarouselNav = () => {
        if (msgType !== 'FC' || carousels.length <= 1) return null;

        const navClass = "absolute top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white shadow-lg " +
            "border border-gray-200 text-gray-700 flex items-center justify-center transition-all " +
            "hover:bg-gray-50 disabled:opacity-30 disabled:shadow-none";

        return (
            <>
                <button
                    onClick={() => moveSlide('prev')}
                    disabled={currentSlide === 0}
                    className={`${navClass} left-1`}
                    title="이전 피드 보기 (순서는 바뀌지 않는다)"
                >
                    <ChevronLeft size={22} />
                </button>
                <button
                    onClick={() => moveSlide('next')}
                    disabled={currentSlide === carousels.length - 1}
                    className={`${navClass} right-1`}
                    title="다음 피드 보기 (순서는 바뀌지 않는다)"
                >
                    <ChevronRight size={22} />
                </button>
            </>
        );
    };

    /**
     * 미리보기 **밖 아래**의 피드 관리 도구 — 현재 위치 점 + 피드 추가 / 삭제 / 전체 피드 보기.
     *
     * 프레임 안에 있을 때는 이 버튼들이 '카카오톡 메시지의 일부' 처럼 보였다.
     * 미리보기는 고객이 받는 화면이고, 이 버튼들은 만드는 사람의 도구다 — 밖에 둔다.
     *
     * ⚠️ '삭제' 도 함께 내렸다. 사용자가 지목한 것은 '피드 추가'·'전체 피드 보기' 두 개지만
     *    셋은 한 묶음(2열 그리드)이라 하나만 남기면 프레임 안에 버튼 한 개가 떠 있게 된다.
     */
    const renderCarouselFeedTools = () => {
        if (msgType !== 'FC') return null;

        return (
            <div className="mt-3 flex flex-col gap-3">
                {/* 현재 보고 있는 피드 위치 */}
                <div className="flex justify-center space-x-1.5">
                    {carousels.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentSlide(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${idx === currentSlide ? 'bg-indigo-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                            title={`${idx + 1}번째 피드 보기`}
                        />
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-2">
                    {/* 피드 추가 */}
                    {carousels.length < 6 ? (
                        <button
                            onClick={() => {
                                setCarousels([...carousels, {
                                    id: `feed-${Date.now()}`, header: '', message: '',
                                    attachment: { image: null, buttons: [{ type: 'LINK', name: '', urlMobile: '' }] }
                                }]);
                                setTimeout(() => setCurrentSlide(carousels.length), 100);
                            }}
                            className="col-span-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg flex items-center justify-center gap-2 text-xs font-bold transition-colors border border-gray-200 border-dashed"
                        >
                            <Plus size={14} /> 피드 추가
                        </button>
                    ) : <div className="hidden"></div>}

                    {/* 피드 삭제 */}
                    {carousels.length > 2 ? (
                        <button
                            onClick={() => {
                                if(window.confirm('현재 피드를 삭제하시겠습니까?')) {
                                    const newItems = carousels.filter((_, i) => i !== currentSlide);
                                    setCarousels(newItems);
                                    setCurrentSlide(prev => Math.min(prev, newItems.length - 1));
                                }
                            }}
                            className="col-span-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg flex items-center justify-center gap-2 text-xs font-bold transition-colors border border-red-100 border-dashed"
                        >
                            <Trash2 size={14} /> 삭제
                        </button>
                    ) : <div className="hidden"></div>}

                    <button
                        onClick={() => setIsAllFeedsModalOpen(true)}
                        className="col-span-2 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center gap-2 text-xs font-bold transition-colors border border-indigo-100"
                    >
                        <LayoutGrid size={14} /> 전체 피드 보기
                    </button>
                </div>
            </div>
        );
    };

    // 4. 프리미엄 동영상
    const renderVideo = () => (
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 relative">
            <div className="aspect-video bg-black flex items-center justify-center relative group">
                <Video className="text-white/50 w-16 h-16" />
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <input
                        type="text"
                        className={`w-full bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm mb-2 outline-none${hl('video', true)}`}
                        placeholder="KakaoTV URL 입력"
                        value={video?.videoUrl ?? ''}
                        onChange={(e) => setVideo({videoUrl: e.target.value})}
                    />
                    <a href="https://tv.kakao.com" target="_blank" rel="noreferrer"
                       className="text-xs text-yellow-400 hover:underline flex items-center">
                        카카오TV 등록하러 가기 <ExternalLink size={10} className="ml-1"/>
                    </a>
                </div>
            </div>
            <div className="p-4">
                <input
                    className="w-full font-bold text-gray-800 mb-1 outline-none placeholder-gray-300"
                    placeholder="타이틀 (선택, 20자)"
                    value={header}
                    onChange={(e) => {
                        const val = handleTextChange(e.target.value, RULES.VIDEO.titleMax);
                        if(val !== undefined) setHeader(val);
                    }}
                />
                <div className="text-right text-[10px] text-gray-400 mb-1">
                    {getWeightedLength(header)} / 20
                </div>

                <textarea
                    ref={(el) => handleResizeHeight(el)}
                    className="w-full resize-none text-sm text-gray-600 outline-none placeholder-gray-300 overflow-hidden"
                    placeholder="내용 (선택, 76자)"
                    rows={2}
                    value={content}
                    onInput={(e) => handleResizeHeight(e.target)}
                    onChange={(e) => {
                        const val = handleTextChange(e.target.value, RULES.VIDEO.contentMax, RULES.VIDEO.maxLine);
                        if(val !== undefined) setContent(val);
                    }}
                />
                <div className="text-right text-[10px] text-gray-400">
                    {getWeightedLength(content)} / 76
                </div>
            </div>
        </div>
    );


    const BenefitSelector = ({ couponConfig, setCouponConfig }) => {
        const [benefitType, setBenefitType] = useState("issue")
        const [benefits, setBenefits] = useState([])

        useEffect(() => {
            listBenefit(1, 100, "ACTIVE").then(({data}) => {
                // 예전에는 안내용 가짜 항목 `{name: '쿠폰을 선택하세요'}` 를 목록에 끼워 넣었다.
                // id 가 없어 `key`/`value` 가 undefined 가 되고 React 가 key 경고를 냈다 → 아래 고정 option 으로 분리.
                setBenefits(data.benefits ?? [])
            })
        }, []);

        return (
            <>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-600">첨부할 혜택</label>
                    <select
                        // `selected` 를 option 에 주면 React 가 경고한다 → select 를 value 로 제어한다.
                        value={couponConfig.benefitId ?? ''}
                        onChange={(e) => setCouponConfig({...couponConfig, benefitId: e.target.value || undefined})}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value="" disabled>혜택을 선택하세요</option>
                        {benefits.map(benefit => (
                            // 혜택 타입(링크 혜택형/직원 확인형 …)을 함께 보여준다 — 이름만으로는 어떤 방식인지 알 수 없다.
                            <option key={benefit.id} value={benefit.id}>
                                {benefit.name}{benefit.typeKor ? ` · ${benefit.typeKor}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-span-4 flex flex-col justify-center items-center mb-4 md:mb-0">
                    <div className="w-full max-w-xs bg-white rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500">
                            <CalendarDays className="w-3 h-3" />
                            유효기간 타입
                        </div>
                        <select
                            value={benefitType}
                            onChange={(e) => {
                                const type = e.target.value

                                setBenefitType(type)

                                setCouponConfig({
                                    ...couponConfig,
                                    periodType: type,
                                    ...(type === 'issue'
                                            ? { benefitExpiredDate: null }
                                            : { benefitExpiredDay: null }
                                    )
                                })
                            }}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-1.5 mb-2"
                        >
                            <option value="issue">발급일 기준 설정</option>
                            <option value="date">날짜 직접 입력</option>
                        </select>

                        {/* 발급일 기준 입력창 */}
                        {benefitType === 'issue' && (
                            <div className="flex items-center gap-2 animate-fadeIn w-full">
                                <input
                                    type="number"
                                    min="1"
                                    value={couponConfig?.benefitExpiredDay ?? ''}
                                    onChange={(e) => setCouponConfig({...couponConfig, benefitExpiredDay: e.target.value})}
                                    className="flex-1 text-center text-sm border border-gray-300 rounded-md py-1.5 focus:ring-2 focus:ring-indigo-500 outline-none w-full"
                                    placeholder="일수"
                                />
                                <span className="text-xs text-gray-600 whitespace-nowrap">일간 사용</span>
                            </div>
                        )}

                        {/* 날짜 직접 입력 선택 시 노출 */}
                        {benefitType === 'date' && (
                            <div className="flex items-center gap-2 animate-fadeIn w-full">
                                <input
                                    type="date"
                                    min={moment().format('YYYY-MM-DD')} // 오늘 이전 날짜 선택 불가
                                    value={couponConfig?.benefitExpiredDate ?? ''}
                                    onChange={(e) => setCouponConfig({...couponConfig, benefitExpiredDate: e.target.value})}
                                    className="flex-1 text-center text-sm border border-gray-300 rounded-md py-1.5 px-2 focus:ring-2 focus:ring-indigo-500 outline-none w-full"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </>
        )
    }

    // --- [메인 렌더링] ---
    return (
        <div className="bg-gray-50 p-4 md:p-8 font-sans flex flex-col md:flex-row gap-8 justify-center items-start min-w-[350px]">
            <div className="flex-1 max-w-[400px]">
                {/*
                  🔑 미리보기 프레임을 감싸는 `relative` 한 겹 — 캐러셀의 이전/다음 화살표를
                     프레임 **양옆에** 절대배치하기 위한 기준점이다(2026-09-10).
                     프레임 자체에 붙이면 안쪽 스크롤 영역(`overflow-y-auto`) 기준이 되어
                     스크롤할 때 화살표가 같이 밀려 올라간다.
                */}
                <div className="relative">
                    <div className="bg-[#A9BDCE] rounded-[1rem] p-4 shadow-xl border border-gray-300 min-h-[600px] flex flex-col relative">
                        <div className="flex items-center space-x-2 mb-4 opacity-80">
                            <div className="w-8 h-8 rounded-full bg-indigo-200"></div>
                            <div className="text-xs text-gray-700 font-medium">(광고) 오모플레이</div>
                        </div>
                        <div className={`flex-1 overflow-y-auto hide-scrollbar pb-10${msgType === "FI" ? ' pr-8' : ''}`}>
                            {msgType === 'FL' ? renderWideList() :
                                msgType === 'FC' ? renderCarousel() :
                                    msgType === 'FP' ? renderVideo() :
                                        renderBaseOrWide()}
                            {msgType !== 'FC' && renderButtonList(buttons)}
                        </div>
                    </div>
                    {renderCarouselNav()}
                </div>
                {renderCarouselFeedTools()}
            </div>
            {isBtnModalOpen && <AddButtonModal />}
            {/* [New] 전체 피드 보기 모달 렌더링 */}
            {isAllFeedsModalOpen && <AllFeedsModal />}
        </div>
    );
};

export default OneTimeMessageCreator;